import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import UndoIcon from '@mui/icons-material/Undo'
import ScheduleIcon from '@mui/icons-material/Schedule'
import EditNoteIcon from '@mui/icons-material/EditNote'
import moment from 'moment'
import { getAction, getMergedTimeline, retimeEvent, voidEvent } from './engine'
import {
  describeLiveEvent,
  fmtOffset,
  fmtTime,
  liveStartOf,
  logKeyForEvent,
  requestForEvent,
  toneOf,
} from './liveUi'
import useLiveRecord from './useLiveRecord'

const COLLAPSED_COUNT = 8

export const RetimeDialog = ({ event, onClose, onSave }) => {
  const [value, setValue] = useState(
    event ? moment(event.time).format('HH:mm:ss') : '',
  )
  if (!event) return null
  const save = () => {
    const [h, m, s] = value.split(':').map(Number)
    if ([h, m].some((part) => Number.isNaN(part))) return
    const next = moment(event.time)
      .hours(h)
      .minutes(m)
      .seconds(s || 0)
      .milliseconds(0)
    // A time "later" than now on the same day most likely meant yesterday
    if (next.isAfter(moment().add(5, 'minutes'))) next.subtract(1, 'day')
    onSave(next.toISOString())
  }
  return (
    <Dialog open onClose={onClose} onClick={(e) => e.stopPropagation()}>
      <DialogTitle>Change time - {describeLiveEvent(event)}</DialogTitle>
      <DialogContent>
        <TextField
          type="time"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputProps={{ step: 1 }}
          sx={{ mt: 1, minWidth: 200 }}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>
          Save time
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Recent-events panel on the live console: newest first, with undo, retime
// and add/edit details per item.
const LiveTimeline = ({ onOpenDetails, maxHeight }) => {
  const { formValues, updateRecord } = useLiveRecord()
  const [showAll, setShowAll] = useState(false)
  const [retiming, setRetiming] = useState(null)

  const cardiacArrest = formValues?.cardiacArrest
  const criticalCare = formValues?.criticalCare
  const items = useMemo(
    () => getMergedTimeline({ cardiacArrest, criticalCare }).reverse(),
    [cardiacArrest, criticalCare],
  )
  const start = liveStartOf({ cardiacArrest, criticalCare })
  const shown = showAll ? items : items.slice(0, COLLAPSED_COUNT)

  const withLog = (eventId, fn) =>
    updateRecord((record) => {
      const logKey = logKeyForEvent(record, eventId)
      return logKey ? fn(record, logKey) : record
    })

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography fontWeight={800}>Timeline</Typography>
        {items.length > COLLAPSED_COUNT && (
          <Button size="small" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? 'Show recent' : `Show all (${items.length})`}
          </Button>
        )}
      </Stack>
      {!items.length && (
        <Typography color="text.secondary">
          Taps appear here as you log them.
        </Typography>
      )}
      <Stack
        gap={0.5}
        sx={{ maxHeight, overflowY: maxHeight ? 'auto' : undefined }}
      >
        {shown.map((event) => {
          const action = getAction(event.type)
          const tone = toneOf(action?.tone)
          const detailRequest = requestForEvent(event)
          return (
            <Stack
              key={event.id || `${event.type}-${event.time}`}
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                borderLeft: 4,
                borderLeftColor: tone.main,
                pl: 1,
                py: 0.25,
              }}
            >
              <Box sx={{ minWidth: 72 }}>
                <Typography fontWeight={700} fontFamily="monospace">
                  {fmtTime(event.time)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontFamily="monospace"
                >
                  {fmtOffset(event.time, start)}
                </Typography>
              </Box>
              <Typography
                flex={1}
                minWidth={0}
                sx={{ overflowWrap: 'anywhere' }}
              >
                {describeLiveEvent(event)}
              </Typography>
              {event.id && (
                <Stack direction="row">
                  {detailRequest && (
                    <Tooltip title="Add / edit details">
                      <IconButton onClick={() => onOpenDetails(detailRequest)}>
                        <EditNoteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Change time">
                    <IconButton onClick={() => setRetiming(event)}>
                      <ScheduleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Undo">
                    <IconButton
                      color="error"
                      onClick={() =>
                        withLog(event.id, (record, logKey) =>
                          voidEvent(record, logKey, event.id),
                        )
                      }
                    >
                      <UndoIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          )
        })}
      </Stack>
      {retiming && (
        <RetimeDialog
          event={retiming}
          onClose={() => setRetiming(null)}
          onSave={(time) => {
            withLog(retiming.id, (record, logKey) =>
              retimeEvent(record, logKey, retiming.id, time),
            )
            setRetiming(null)
          }}
        />
      )}
    </Paper>
  )
}

export default LiveTimeline
